// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { PartnerAuthProvider } from './contexts/PartnerAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import { FloatingContactProvider } from './components/FloatingContact';
import ProtectedRoute from './components/ProtectedRoute';
import PartnerProtectedRoute from './components/partner/PartnerProtectedRoute';
import ScrollToHashElement from './components/ScrollToHashElement';
import ScrollToTop from './components/ScrollToTop';
import './styles/variables.css';
import './styles/PartnerPortal.css';
import './App.css';

// Lazy load page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const DownloadsPage = lazy(() => import('./pages/DownloadsPage'));
const StartNowPage = lazy(() => import('./pages/StartNowPage'));
const ServicePickerPage = lazy(() => import('./pages/ServicePickerPage'));
const BuildPackagePage = lazy(() => import('./pages/BuildPackagePage'));
const CollaborationPage = lazy(() => import('./pages/CollaborationPage'));
const ServiceFormPage = lazy(() => import('./pages/ServiceFormPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ServiceCatalogPage = lazy(() => import('./pages/ServiceCatalogPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Partner Portal Pages
const PartnerLogin = lazy(() => import('./pages/partner/PartnerLogin'));
const PartnerDashboard = lazy(() => import('./pages/partner/PartnerDashboard'));
const PartnerMeetings = lazy(() => import('./pages/partner/PartnerMeetings'));
const PartnerOrders = lazy(() => import('./pages/partner/PartnerOrders'));
const PartnerInvoices = lazy(() => import('./pages/partner/PartnerInvoices'));
const PartnerContract = lazy(() => import('./pages/partner/PartnerContract'));
const PartnerSettings = lazy(() => import('./pages/partner/PartnerSettings'));
const PartnerAgreements = lazy(() => import('./pages/partner/PartnerAgreements'));
const PartnerReferrals = lazy(() => import('./pages/partner/PartnerReferrals'));
const PartnerBookMeeting = lazy(() => import('./pages/partner/PartnerBookMeeting'));

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

// 404 Not Found page
const NotFoundPage = () => (
  <div style={{ textAlign: 'center', padding: '80px 20px' }}>
    <h1>404</h1>
    <p>Page not found</p>
    <a href="/">Return to Home</a>
  </div>
);

// Partner Portal Loading Component
const PartnerLoader = () => (
  <div className="partner-portal" style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#1a1614'
  }}>
    <div className="partner-loading">
      <div className="partner-spinner"></div>
      <div className="partner-loading-text" style={{ color: '#c4b8ae' }}>Loading...</div>
    </div>
  </div>
);

// Layout component that conditionally renders Header/Footer
function Layout() {
  const location = useLocation();
  const hideFooterPaths = ['/dashboard'];
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const shouldHideFooter = hideFooterPaths.includes(location.pathname) || isPartnerRoute;

  // Partner portal routes - completely separate layout
  if (isPartnerRoute) {
    return (
      <PartnerAuthProvider>
        <Suspense fallback={<PartnerLoader />}>
          <Routes>
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/dashboard" element={
              <PartnerProtectedRoute><PartnerDashboard /></PartnerProtectedRoute>
            } />
            <Route path="/partner/meetings" element={
              <PartnerProtectedRoute><PartnerMeetings /></PartnerProtectedRoute>
            } />
            <Route path="/partner/orders" element={
              <PartnerProtectedRoute><PartnerOrders /></PartnerProtectedRoute>
            } />
            <Route path="/partner/invoices" element={
              <PartnerProtectedRoute><PartnerInvoices /></PartnerProtectedRoute>
            } />
            <Route path="/partner/contract" element={
              <PartnerProtectedRoute><PartnerContract /></PartnerProtectedRoute>
            } />
            <Route path="/partner/agreements" element={
              <PartnerProtectedRoute><PartnerAgreements /></PartnerProtectedRoute>
            } />
            <Route path="/partner/referrals" element={
              <PartnerProtectedRoute><PartnerReferrals /></PartnerProtectedRoute>
            } />
            <Route path="/partner/book-meeting" element={
              <PartnerProtectedRoute><PartnerBookMeeting /></PartnerProtectedRoute>
            } />
            <Route path="/partner/settings" element={
              <PartnerProtectedRoute><PartnerSettings /></PartnerProtectedRoute>
            } />
            <Route path="/partner" element={<PartnerLogin />} />
          </Routes>
        </Suspense>
      </PartnerAuthProvider>
    );
  }

  // Main site layout
  return (
    <div className="app">
      <ScrollToHashElement />
      <ScrollToTop />
      <Header />
      <main className="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:postId" element={<BlogPost />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/start-now" element={<StartNowPage />} />
            <Route path="/get-started" element={<ServicePickerPage />} />
            <Route path="/build-package" element={<BuildPackagePage />} />
            <Route path="/collaboration" element={<CollaborationPage />} />
            <Route path="/service-form/:service" element={<ServiceFormPage />} />
            <Route path="/service-form" element={<ServiceFormPage />} />
            <Route path="/catalog" element={<ServiceCatalogPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!shouldHideFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <FloatingContactProvider>
            <Layout />
          </FloatingContactProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
